// src/pages/Users/Galleries/components/AlbumDetailHero.jsx

import React, { useMemo } from "react";
import { FaCalendarAlt, FaEye, FaImages, FaStar } from "react-icons/fa";

import {
  countPhotos,
  getCoverUrl,
  stripHtml,
} from "../utils/galleriesUtils";

const FALLBACK = "/img/no-image.jpg";

function onImgError(event) {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK;
  }
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.valueOf())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function AlbumDetailHero({ gallery, photos = [] }) {
  const coverUrl = getCoverUrl(gallery);

  const description = useMemo(
    () => stripHtml(gallery?.description || ""),
    [gallery?.description]
  );

  const coverPhoto = photos.find((photo) => photo?.is_cover);

  return (
    <section className="relative overflow-hidden rounded-[2rem] bg-slate-950 text-white shadow-xl shadow-slate-950/10">
      <div className="absolute inset-0">
        <img
          src={coverUrl}
          alt={gallery?.titre || "Album photo"}
          className="h-full w-full object-cover opacity-45"
          loading="eager"
          onError={onImgError}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/35" />
      </div>

      <div className="relative p-5 sm:p-6 lg:p-8">
        <div className="max-w-3xl">
          <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white ring-1 ring-white/20">
            <FaImages />
            Album photos
          </p>

          <h2 className="mt-4 text-3xl font-bold leading-tight sm:text-4xl">
            {gallery?.titre || "Album sans titre"}
          </h2>

          {description ? (
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
              {description}
            </p>
          ) : (
            <p className="mt-3 text-sm leading-7 text-slate-300">
              Album photo du club.
            </p>
          )}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <HeroStat
            icon={FaImages}
            label="Photos"
            value={countPhotos(gallery)}
          />
          <HeroStat
            icon={FaEye}
            label="Statut"
            value={gallery?.is_active ? "Visible" : "Masqué"}
          />
          <HeroStat
            icon={FaStar}
            label="Couverture"
            value={coverPhoto ? "OK" : "Auto"}
          />
          <HeroStat
            icon={FaCalendarAlt}
            label="Mise à jour"
            value={formatDate(gallery?.updated_at || gallery?.created_at)}
          />
        </div>
      </div>
    </section>
  );
}

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-300">
        <Icon />
        {label}
      </div>

      <p className="mt-2 text-lg font-bold text-white">{value}</p>
    </div>
  );
}